import {
  getMaterialInfoPR,
  getMaterialInfoKit,
} from '@/repositories/Materials';
import { getBackorders, deleteBackorder } from '@/repositories/Backorders';
import { getUniqueID } from '@/utilities/helpers';
import { Roles } from '@/core/constants/roleConstants';

export default {
  namespaced: true,
  state: {
    reminders: [],
    selectedReminders: [],
    loading: false,
    error: null,
    isDelete: null,
    isDeleteActive: false,
    roles: [
      'Seller',
      'Customer',
      'CustomerPR',
      'Manager',
      'Submanager',
      'RegionalManager',
      'IELExpressSales',
      'CreditAssistant',
      'Subdirector',
      'SubDirectorPRNoCommercial',
      'SubDirectorDTNoCommercial',
      'Director',
      'DirectorNoCommercial',
      'Superuser',
      'IELMarketing',
      Roles.POST_SALE,
    ], // Roles that can use this module
  },

  getters: {
    reminders: (state) => state.reminders,
    remindersCount: (state) => state.reminders.length,
    hasReminders: (state, getters) => getters.remindersCount > 0,
    loadingReminders: (state) => state.loading,
    selectedReminders: (state) => state.selectedReminders,
    hasRemindersSelected: (state) => state.selectedReminders.length > 0,
    isDeleteReminders: (state) => state.isDelete,
    isDeleteActive: (state) => state.isDeleteActive,
    firstReminderSelected: (state, getters) => {
      if (getters.hasRemindersSelected) {
        return state.selectedReminders[0];
      }
      return null;
    },
    firstReminderSelectedIsKit: (state, getters) => {
      if (getters.firstReminderSelected !== null) {
        return getters.firstReminderSelected.isKit;
      }
      return false;
    },
    availableReminders: (getters) => {
      return getters.reminders.filter(
        (r) =>
          reminderHasStock(r.status) && r.backorderProgrammedDisabled == false
      );
    },
    availableRemindersComplete: (getters) => {
      return getters.reminders.filter(
        (r) =>
          reminderHasStockComplete(r.status) &&
          r.backorderProgrammedDisabled == false
      );
    },
    availableRemindersCount: (state, getters) =>
      getters.availableRemindersComplete.length,
    hasAvailableReminders: (state, getters) =>
      getters.availableRemindersCount > 0,
    blockedUser: (state, getters, rootState, rootGetters) => {
      const roles = rootGetters['auth/roles'];
      const isSeller = roles.some((role) => role.name === 'Seller');

      if (isSeller) return false;

      return getters.availableReminders.length > 0;
    },
    isReminderWithErrors: () => (reminder) => {
      return !!reminder?.hasError;
    },
    isReminderCompatible: (state, getters) => (reminder) => {
      return validateCompatibility(getters.firstReminderSelected, reminder);
    },
    isReminderSelectable: (state, getters) => (reminder) => {
      const isReel = reminder.isReel;
      const hasError = reminder.hasError;
      const isProgrammedDisabled = reminder.backorderProgrammedDisabled;
      const hasStock = validateStock(reminder);
      const isCompatible = validateCompatibility(
        getters.firstReminderSelected,
        reminder
      );

      let isSelectable;
      const isDeleteActive = getters.isDeleteActive;

      if (isDeleteActive) {
        isSelectable = true;
      } else {
        isSelectable =
          hasStock &&
          isCompatible &&
          !isReel &&
          !isProgrammedDisabled &&
          !hasError;
      }

      return isSelectable;
    },
    roles: (state) => state.roles,
  },

  mutations: {
    SET_REMINDERS(state, reminders) {
      state.reminders = [...reminders];
    },
    UPDATE_REMINDER(state, reminderUpdated) {
      const reminderIndex = state.reminders.findIndex(
        (r) => r.id === reminderUpdated.id
      );

      if (reminderIndex >= 0)
        state.reminders.splice(reminderIndex, 1, reminderUpdated);
    },
    SET_REMINDERS_SELECTED(state, reminders) {
      state.selectedReminders = [...reminders];
    },
    REMOVE_ITEM_SELCETED(state, reminder) {
      const reminderIndex = state.selectedReminders.findIndex(
        (r) => r.id === reminder.id
      );

      if (reminderIndex >= 0) state.selectedReminders.splice(reminderIndex, 1);
    },

    UDPATE_ITEM_SELCETED(state, reminder) {
      const reminderIndex = state.selectedReminders.findIndex(
        (r) => r.id === reminder.id
      );

      if (reminderIndex >= 0)
        state.selectedReminders.splice(reminderIndex, 1, reminder);
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error = null) {
      state.error = error;
    },
    SET_IS_DELETE_PROCESS(state, data) {
      state.isDelete = data;
    },
    SET_SELECTED_REMINDERS(state, reminders) {
      state.selectedReminders = [...reminders];
    },
    SET_IS_DELETE_ACTIVE(state, isActive) {
      state.isDeleteActive = isActive;
    },
    REMOVE_REMINDERS(state, reminderIds) {
      state.reminders = state.reminders.filter(
        (reminder) => !reminderIds.includes(reminder.id)
      );
    },
    REMOVE_SELECTED_REMINDERS(state, reminderIds) {
      state.selectedReminders = state.selectedReminders.filter(
        (reminder) => !reminderIds.includes(reminder.id)
      );
    },
  },
  actions: {
    async loadReminders({ commit, rootGetters, dispatch, state }) {
      const userId = rootGetters['auth/userCode'];
      const hasRoles = await dispatch('auth/hasAnyRole', state.roles, {
        root: true,
      });

      if (userId && hasRoles) {
        commit('SET_LOADING', true);
        commit('SET_ERROR');

        const channel = rootGetters['auth/channel'];
        const roles = rootGetters['auth/roles'][0].name;
        const office = rootGetters['auth/user'].VKBUR;

        try {
          const reminders = await getBackorders(userId, channel, roles, office);

          // SETS A UNIQUE ID TO PREVENT VUE FROM CRASHING IF THE ITEM DOESNT HAVE AN ID TO USE AS KEY
          const normalizedReminders = reminders.map((r) => ({
            ...r,
            uniqueId: getUniqueID(),
          }));

          commit('SET_REMINDERS', normalizedReminders);
        } catch (error) {
          commit('SET_ERROR', 'Hubo un error al recuperar los recordatorios');
        }

        commit('SET_LOADING', false);
      } else {
        commit('SET_REMINDERS', []); // If user is null reminders list is cleaned up
        commit('SET_REMINDERS_SELECTED', []);
      }
    },
    async removeReminder({ commit }, { reminders, reasonsForm }) {
      await deleteBackorder(reminders, reasonsForm);
      const idsToRemove = reminders.remindersData.map(
        (reminder) => reminder.id
      );

      commit('REMOVE_REMINDERS', idsToRemove);
      commit('SET_REMINDERS_SELECTED', []);
    },
    cleanReminders({ commit }) {
      commit('SET_REMINDERS', []);
      commit('SET_REMINDERS_SELECTED', []);
    },
    updateRemindersSelectionList({ commit }, newList) {
      commit('SET_REMINDERS_SELECTED', newList);
    },
    async validateItem({ getters, commit, rootGetters }, reminder) {
      if (
        !matchFirstReminderSelected(getters.firstReminderSelected, reminder)
      ) {
        commit('REMOVE_ITEM_SELCETED', reminder);
      } else {
        const authUser = rootGetters['auth/user'];

        const customer = {
          customer: reminder.customer.code,
          channel: authUser.VTWEG,
          VTWEG: authUser.VTWEG,
          VKBUR: authUser.VKBUR,
          VKORG: authUser.VKORG,
        };

        try {
          if (reminder.isKit) {
            await getMaterialInfoKit(
              reminder.materialId,
              reminder.requiredQty,
              customer
            ); // Just to try catch any
            // commit('UDPATE_ITEM_SELCETED', reminder);
          } else {
            const material = await getMaterialInfoPR(
              reminder.materialId,
              reminder.requiredQty,
              customer
            );
            const newMaterial = {
              ...reminder,
              reminders: material.recordatorios,
              priceList: material.precio_lista,
              discountPrice: material.precio_con_descuento,
              productPrice: material.importe_producto,
            };

            commit('UDPATE_ITEM_SELCETED', newMaterial);
          }
        } catch (error) {
          const defaultErrorMessage = 'No se pudo seleccionar el backorder';

          commit('REMOVE_ITEM_SELCETED', reminder);

          reminder.hasError = true;
          commit('UPDATE_REMINDER', reminder);

          if (error?.response?.status == 404) {
            return error.response.data?.error_code || defaultErrorMessage;
          }
          if (error?.response?.status == 400) {
            return error.response.data?.message || defaultErrorMessage;
          } else {
            return defaultErrorMessage;
          }
        }
      }

      return null;
    },

    getUserReminders: ({ getters }, userId) => {
      return getters.reminders.filter((r) => r.user.code == userId);
    },

    getUserAvailableReminders: ({ getters }, userId) => {
      return getters.reminders.filter(
        (r) =>
          r.customer.code == userId &&
          reminderHasStock(r.status) &&
          r.backorderProgrammedDisabled == false
      );
    },

    updateDelete({ commit }, data) {
      commit('SET_IS_DELETE_PROCESS', data);
      return data;
    },

    isDeleteUncheck: ({ commit }, reminders) => {
      const reminder = [];
      for (let i = 0; i < reminders.length; i++) {
        reminder.push(reminders[i]);
      }
      reminder.forEach((item) => {
        commit('REMOVE_ITEM_SELCETED', item);
      });
    },
    updateSelectedReminders({ commit }, reminders) {
      commit('SET_SELECTED_REMINDERS', reminders);
    },
    clearSelectedReminders({ commit }) {
      commit('SET_SELECTED_REMINDERS', []);
    },
    updateIsDeleteActive({ commit }, isActive) {
      commit('SET_IS_DELETE_ACTIVE', isActive);
      return isActive;
    },
  },
};

const reminderHasStock = (status) => {
  return status === 'VERDE';
};

const reminderHasStockComplete = (status) => {
  return status === 'AMARILLO' || status === 'VERDE';
};

const matchFirstReminderSelected = (firstSelectedReminder, newReminder) => {
  if (!firstSelectedReminder) return true;

  if (firstSelectedReminder.isKit && !newReminder.isKit) return false; // If the first material is KIT, the rest should be kits

  if (!firstSelectedReminder.isKit && newReminder.isKit) return false; // If the first material is not KIT, the rest should not be kits
  if (firstSelectedReminder.type != 'RE' && newReminder.type == 'RE')
    return false; // If the first material is not Reminder, the rest should not be Reminders
  if (firstSelectedReminder.type != 'PR' && newReminder.type == 'PR')
    return false; // If the first material is not Reminder, the rest should not be Reminders

  if (firstSelectedReminder.customer.code === newReminder.customer.code)
    return true; // Seller can add materials only for the same customer

  return false;
};

const validateCompatibility = (firstSelected, reminder) => {
  if (!firstSelected) return true;
  if (firstSelected.isKit !== reminder.isKit) return false;

  const type = firstSelected.type;
  if (type !== reminder.type) return false;

  const customerCode = firstSelected.customer?.code;
  if (customerCode !== reminder.customer?.code) return false;

  return true;
};

const validateStock = (reminder) => {
  const status = reminder.status;
  return status === 'VERDE' || status === 'AMARILLO';
};
